/**
 * Configurazione i18next. UN SOLO stack di traduzione.
 * (Il portale statico ne aveva quattro coesistenti: ic-i18n-engine.js,
 *  ic-translations.js, language-manager.js, ic-lang-widget.js — con il
 *  dropdown legacy nascosto via `display:none!important` invece di rimosso.)
 *
 * Scelte dichiarate:
 *  · `keySeparator: false` e `nsSeparator: false` — le chiavi migrate sono piatte
 *    e contengono `_`; senza questo, `comune_tab_map` verrebbe letto come
 *    percorso annidato.
 *  · Un solo namespace (`translation`). Niente split per pagina: 597 chiavi
 *    sono ~32 KB per lingua, e ne carichiamo UNA alla volta.
 *  · Caricamento lazy per lingua con `import()`: Vite emette un chunk per
 *    locale, quindi si scarica solo la lingua attiva. Nessun cache-busting
 *    manuale (il vecchio motore aveva `I18N_VERSION = 'v20260614-blue20'`
 *    da bumpare a mano).
 *  · `returnNull: false` — una chiave mancante restituisce la chiave stessa,
 *    non `null`: non si stampa mai "null" in pagina.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { FALLBACK_LANGUAGE, LANGUAGES, LANGUAGE_CODES } from './languages.generated';
import type { LanguageMeta } from './languages.generated';
import itTranslations from './locales/it.json';

export const STORAGE_KEY = 'ic-lang';

/**
 * Import dinamici espliciti: Vite li risolve staticamente in 10 chunk, uno per
 * lingua. `it` NON è qui: è importato staticamente sopra perché è il fallback
 * e deve essere sempre disponibile (metterlo anche fra i dinamici produrrebbe
 * il warning INEFFECTIVE_DYNAMIC_IMPORT e nessun chunk separato).
 */
const LOADERS: Record<string, () => Promise<{ default: Record<string, string> }>> = {
  en: () => import('./locales/en.json'),
  fr: () => import('./locales/fr.json'),
  de: () => import('./locales/de.json'),
  es: () => import('./locales/es.json'),
  pt: () => import('./locales/pt.json'),
  nl: () => import('./locales/nl.json'),
  pl: () => import('./locales/pl.json'),
  ru: () => import('./locales/ru.json'),
  ja: () => import('./locales/ja.json'),
  zh: () => import('./locales/zh.json'),
};

export function isSupportedLanguage(code: string | null | undefined): boolean {
  return typeof code === 'string' && LANGUAGE_CODES.includes(code);
}

export function languageMeta(code: string): LanguageMeta {
  return LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0]!;
}

/**
 * Ordine di risoluzione della lingua, dichiarato:
 *  1. segmento di URL `?lang=xx` (usato dai QR code del totem)
 *  2. localStorage
 *  3. `navigator.language`
 *  4. fallback `it`
 */
export function detectLanguage(search = window.location.search): string {
  const fromQuery = new URLSearchParams(search).get('lang');
  if (isSupportedLanguage(fromQuery)) return fromQuery!;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isSupportedLanguage(stored)) return stored!;
  } catch {
    // localStorage può essere negato (Safari privato, kiosk lockdown).
  }

  const nav = navigator.language?.slice(0, 2).toLowerCase();
  if (isSupportedLanguage(nav)) return nav;

  return FALLBACK_LANGUAGE;
}

/** Carica il dizionario di una lingua se non è già in memoria. */
export async function loadLanguage(code: string): Promise<void> {
  if (!isSupportedLanguage(code)) return;
  if (i18next.hasResourceBundle(code, 'translation')) return;
  const loader = LOADERS[code];
  if (!loader) return;
  const mod = await loader();
  i18next.addResourceBundle(code, 'translation', mod.default, true, true);
}

/** Cambia lingua, aggiorna `<html lang>`/`dir` e persiste la scelta. */
export async function changeLanguage(code: string): Promise<void> {
  if (!isSupportedLanguage(code)) return;
  await loadLanguage(code);
  await i18next.changeLanguage(code);
  applyDocumentLanguage(code);
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* storage negato: la lingua resta valida per la sessione */
  }
}

/** WCAG 3.1.1 — l'attributo `lang` del documento deve seguire la lingua reale. */
export function applyDocumentLanguage(code: string): void {
  const meta = languageMeta(code);
  document.documentElement.lang = code;
  document.documentElement.dir = meta.dir;
}

export async function initI18n(initialLanguage = detectLanguage()): Promise<void> {
  await i18next.use(initReactI18next).init({
    // `it` è sempre in bundle: è il fallback e non deve mai mancare.
    resources: { it: { translation: itTranslations as Record<string, string> } },
    lng: FALLBACK_LANGUAGE,
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: [...LANGUAGE_CODES],
    defaultNS: 'translation',
    ns: ['translation'],
    keySeparator: false,
    nsSeparator: false,
    returnNull: false,
    interpolation: {
      // React esegue già l'escaping: il doppio escaping romperebbe gli apostrofi
      // italiani ("dell'opportunità").
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

  if (initialLanguage !== FALLBACK_LANGUAGE) {
    await changeLanguage(initialLanguage);
  } else {
    applyDocumentLanguage(FALLBACK_LANGUAGE);
  }
}

export { LANGUAGES, LANGUAGE_CODES, FALLBACK_LANGUAGE };
export type { LanguageMeta };
export default i18next;
